<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" @click="$emit('close')"></div>
    </Transition>

    <Transition name="slide-over">
      <div v-if="open" class="fixed inset-y-0 right-0 z-50 w-full sm:w-[85%] bg-white dark:bg-[#1C2128] shadow-2xl flex flex-col">

        <!-- Header -->
        <div class="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="min-w-0">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white truncate">
              {{ isEdit ? `Edit v${releaseNote.version}` : 'New release' }}
            </h2>
            <p class="text-xs text-gray-400 dark:text-gray-500 truncate">
              Published to the public changelog at /release-notes
            </p>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <a v-if="isEdit" :href="`/release-notes/${releaseNote.slug}`" target="_blank" rel="noopener"
              class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
              </svg>
              Preview
            </a>
            <button type="button" @click="$emit('close')"
              class="px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button type="button" @click="submit('save')" :disabled="saving || !canSave"
              class="px-4 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors">
              {{ saving ? 'Saving...' : saveLabel }}
            </button>

            <button v-if="!isLive" type="button" @click="submit('publish')" :disabled="saving || !canPublish"
              class="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors">
              {{ publishLabel }}
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="flex-1 min-h-0 overflow-y-auto">
          <div class="mx-auto max-w-3xl px-5 sm:px-6 py-6 space-y-6">

            <p v-if="error" class="px-3.5 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm font-medium text-red-600 dark:text-red-400">
              {{ error }}
            </p>

            <!-- Title -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Title<span class="text-red-500">*</span>
              </label>
              <input ref="titleInput" v-model="title" type="text" autocomplete="off"
                placeholder="e.g. Team scheduling and faster booking pages"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                <template v-if="isEdit">
                  Public address: <span class="font-mono">/release-notes/{{ releaseNote.slug }}</span>.
                  <span v-if="isLive">Fixed, because the release is published.</span>
                </template>
                <template v-else-if="slugPreview">
                  Public address: <span class="font-mono">/release-notes/{{ slugPreview }}</span>
                </template>
              </p>
            </div>

            <!-- Summary -->
            <div>
              <div class="flex items-baseline justify-between mb-1.5">
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Summary<span class="text-red-500">*</span>
                </label>
                <span class="text-xs tabular-nums" :class="summary.length > SUMMARY_LIMIT ? 'text-red-500 font-semibold' : 'text-gray-400 dark:text-gray-500'">
                  {{ summary.length }}/{{ SUMMARY_LIMIT }}
                </span>
              </div>
              <textarea v-model="summary" rows="3"
                placeholder="One or two sentences. This is the card on the index, the search result, and the social preview."
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-y"></textarea>
            </div>

            <!-- Release type. Every option carries the version it would
                 actually produce, so the author picks how big the release is
                 rather than typing a number. -->
            <div v-if="!isEdit">
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Release type
              </label>
              <div class="grid grid-cols-3 gap-2">
                <button v-for="option in RELEASE_TYPES" :key="option.value" type="button"
                  @click="releaseType = option.value" :disabled="manualVersion"
                  class="px-3 py-2.5 rounded-lg border text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="releaseType === option.value && !manualVersion
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
                  <span class="block text-sm font-semibold text-gray-900 dark:text-white">{{ option.label }}</span>
                  <span class="mt-0.5 block text-sm font-mono tabular-nums"
                    :class="releaseType === option.value && !manualVersion ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
                    v{{ nextVersions[option.value] }}
                  </span>
                  <span class="mt-0.5 block text-[11px] leading-snug text-gray-400 dark:text-gray-500">{{ option.hint }}</span>
                </button>
              </div>

              <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
                <template v-if="nextVersions.latest">
                  Previous release v{{ nextVersions.latest }}<template v-if="nextVersions.latest_published_at">, published {{ formatDate(nextVersions.latest_published_at) }}</template>.
                </template>
                <template v-else>This is the first release.</template>
              </p>

              <label class="mt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                <input v-model="manualVersion" type="checkbox"
                  class="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                Set the version manually
              </label>
              <input v-if="manualVersion" v-model="version" type="text" placeholder="2.5.0"
                class="mt-2 w-40 px-3 py-2 text-sm font-mono border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>

            <!-- Version is fixed once the release exists: it is the identity
                 of the release, and on a published one it is in the URL of
                 anything that linked to it. -->
            <div v-else>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Version</label>
              <div class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-mono font-semibold bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                v{{ releaseNote.version }}
              </div>
            </div>

            <!-- Tags -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Tags <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span>
              </label>
              <div class="flex flex-wrap gap-2">
                <button v-for="tag in RELEASE_TAGS" :key="tag.value" type="button" @click="toggleTag(tag.value)"
                  class="px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors"
                  :class="tags.includes(tag.value)
                    ? 'border-blue-500 bg-blue-600 text-white'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'">
                  {{ tag.label }}
                </button>
              </div>
            </div>

            <!-- Release date. Editable at every stage, which is what makes
                 backdating and correcting a wrong date possible at all. Empty
                 means "stamp it when I publish". -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Release date
              </label>
              <div class="flex flex-wrap items-center gap-2">
                <input v-model="publishedAt" type="datetime-local"
                  class="px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
                <button v-if="publishedAt" type="button" @click="publishedAt = ''"
                  class="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  Clear
                </button>
                <button type="button" @click="setDateToNow"
                  class="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  Now
                </button>
              </div>
              <p class="mt-1.5 text-xs" :class="dateHintClass">{{ dateHint }}</p>
            </div>

            <!-- Video. One per release, YouTube only. -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Video <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional, one per release)</span>
              </label>
              <input v-model="videoUrl" type="url" autocomplete="off"
                placeholder="https://www.youtube.com/watch?v=..."
                :class="videoUrl && !videoId ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"
                class="w-full px-3.5 py-2.5 text-sm border rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />

              <p v-if="videoUrl && !videoId" class="mt-1.5 text-xs font-medium text-red-500 dark:text-red-400">
                That is not a YouTube link. Paste a watch, share, embed or Shorts URL.
              </p>
              <p v-else class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                Upload to YouTube, then paste the link. It plays at the top of the public page.
              </p>

              <!-- Confirms they pasted the video they meant, before publishing. -->
              <div v-if="videoId" class="mt-3 flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0d1117]">
                <img :src="`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`" alt=""
                  class="w-28 h-[63px] object-cover rounded-lg flex-shrink-0 bg-gray-200 dark:bg-gray-800" />
                <div class="min-w-0">
                  <p class="text-xs font-semibold text-gray-700 dark:text-gray-200">Video attached</p>
                  <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 font-mono break-all">{{ videoId }}</p>
                  <a :href="`https://www.youtube.com/watch?v=${videoId}`" target="_blank" rel="noopener"
                    class="mt-1 inline-block text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                    Open on YouTube
                  </a>
                </div>
              </div>
            </div>

            <!-- Notes -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Notes<span class="text-red-500">*</span>
              </label>
              <quill-editor
                :content="body"
                :enable="true"
                :enable-table="true"
                :upload-image="uploadBodyImage"
                :opts="{ height: '420px' }"
                @update-content="body = $event" />
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                Write what changed for the person using it. Headings become the outline of the public page.
                Keep schema, code and infrastructure out of it.
              </p>
              <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Use the image button in the toolbar, or paste a screenshot straight in. PNG, JPEG, GIF or WEBP,
                up to 5MB. Images are resized in the browser before upload.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { RELEASE_TAGS } from 'stores/admin_release_notes.js'
import { useImageCompression } from 'composables/useImageCompression.js'

const SUMMARY_LIMIT = 300

const RELEASE_TYPES = [
  { value: 'major', label: 'Major', hint: 'A reshaped product' },
  { value: 'minor', label: 'Minor', hint: 'New features' },
  { value: 'patch', label: 'Patch', hint: 'Fixes and polish' },
]

const props = defineProps({
  // Kept mounted between opens so the backdrop fade and the panel slide can
  // animate both in and out — same as AddGlobalComponentSlideOver.
  open: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
  // Null for a new release; the admin-view record when editing.
  releaseNote: { type: Object, default: null },
  nextVersions: { type: Object, required: true },
  // (file, attributes) => Promise<url|null>. Owned by the page, because it may
  // have to create the release before it can attach anything to it, and the
  // page is the only thing that writes to the store.
  attachImage: { type: Function, required: true },
})

const emit = defineEmits(['close', 'submit', 'image-error'])

const { compressImage } = useImageCompression()

const title = ref('')
const summary = ref('')
const body = ref('')
const tags = ref([])
const releaseType = ref('minor')
const manualVersion = ref(false)
const version = ref('')
const publishedAt = ref('')
const videoUrl = ref('')
const titleInput = ref(null)

const isEdit = computed(() => !!props.releaseNote?.id)
const isLive = computed(() => props.releaseNote?.display_status === 'published')
const isScheduled = computed(() => props.releaseNote?.display_status === 'scheduled')

const saveLabel = computed(() => (isEdit.value ? 'Save' : 'Save draft'))

// The button says what will actually happen, which depends entirely on the
// date field: a future date schedules, anything else goes live now.
const publishLabel = computed(() => {
  if (isScheduled.value) return 'Reschedule'
  return isFutureDate.value ? 'Schedule' : 'Publish'
})

const isFutureDate = computed(() => {
  if (!publishedAt.value) return false
  const at = new Date(publishedAt.value)
  return !Number.isNaN(at.getTime()) && at > new Date()
})

const dateHint = computed(() => {
  if (!publishedAt.value) return 'Leave empty to stamp the moment you publish.'
  if (isFutureDate.value) return `Scheduled. It appears on the public page by itself on ${formatDateTime(publishedAt.value)}.`
  if (isLive.value) return `Shown publicly as ${formatDateTime(publishedAt.value)}.`
  return `Backdated. Publishing will show it as ${formatDateTime(publishedAt.value)} and it goes live straight away.`
})

const dateHintClass = computed(() =>
  (isFutureDate.value ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'))

// Mirrors ReleaseNote.extract_youtube_id closely enough to preview the
// thumbnail and catch a typo before saving. The server still has the last word.
const videoId = computed(() => {
  const value = videoUrl.value.trim()
  if (!value) return null
  if (/^[A-Za-z0-9_-]{11}$/.test(value)) return value
  const match = value.match(
    /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtu\.be\/|(?:youtube|youtube-nocookie)\.com\/(?:watch\?(?:\S*&)?v=|embed\/|shorts\/|live\/|v\/))([A-Za-z0-9_-]{11})(?:[?&/#]\S*)?$/,
  )
  return match ? match[1] : null
})

const videoIsValid = computed(() => !videoUrl.value.trim() || !!videoId.value)

const canSave = computed(() =>
  title.value.trim().length > 0 && summary.value.length <= SUMMARY_LIMIT && videoIsValid.value)

// Publishing is the point at which the model starts requiring a summary and a
// body, so the button is gated on the same two fields rather than letting the
// server reject the request.
const canPublish = computed(() =>
  canSave.value && summary.value.trim().length > 0 && plainBodyLength.value > 0)

// Quill leaves "<p><br></p>" behind in an editor the author has cleared, which
// is not blank as far as `body.present?` is concerned but is blank to a reader.
const plainBodyLength = computed(() =>
  body.value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length)

// Mirrors ReleaseNote#assign_slug closely enough to show the author the URL
// they are about to create. The server still has the last word.
const slugPreview = computed(() =>
  title.value.trim().toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80))

// Handed to <quill-editor> as its `uploadImage` prop. Contract: takes the raw
// File, resolves to a URL string on success or null on failure, and never
// rejects — the editor reads null as "leave the document alone".
//
// An image has to belong to a release, or its blob would leak into storage
// with nothing to purge it. A brand new release has no id yet, so the first
// image saves the draft first and carries on with the id that comes back. The
// author sees the header switch from "Save draft" to "Save" and the version
// appear, which is exactly what happened.
async function uploadBodyImage(file) {
  if (!canSave.value) {
    emit('image-error', 'Add a title first, then the image can be attached to the draft.')
    return null
  }

  const compressed = await compressImage(file)
  return props.attachImage(compressed, attributes())
}

function toggleTag(value) {
  tags.value = tags.value.includes(value)
    ? tags.value.filter((t) => t !== value)
    : [...tags.value, value]
}

function formatDate(value) {
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatDateTime(value) {
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// <input type="datetime-local"> speaks local time with no zone and no seconds,
// so an ISO string from the API has to be converted rather than sliced: slicing
// the UTC string would shift the displayed time by the viewer's offset.
function toLocalInputValue(value) {
  if (!value) return ''
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return ''
  const offset = at.getTimezoneOffset() * 60000
  return new Date(at.getTime() - offset).toISOString().slice(0, 16)
}

function setDateToNow() {
  publishedAt.value = toLocalInputValue(new Date())
}

// The panel persists across opens, so the form is reset from props on open
// rather than on mount.
watch(() => props.open, (isOpen) => {
  if (!isOpen) return

  const record = props.releaseNote
  title.value = record?.title ?? ''
  summary.value = record?.summary ?? ''
  body.value = record?.body ?? ''
  tags.value = [...(record?.tags ?? [])]
  releaseType.value = record?.release_type ?? 'minor'
  videoUrl.value = record?.video_url ?? ''
  publishedAt.value = toLocalInputValue(record?.published_at)
  manualVersion.value = false
  version.value = ''

  if (!record) nextTick(() => titleInput.value?.focus())
})

function submit(intent) {
  if (!canSave.value) return
  if (intent === 'publish' && !canPublish.value) return

  emit('submit', { intent, attributes: attributes() })
}

// One definition of the payload, so an autosave triggered by an image upload
// cannot write a different shape than an explicit save.
//
// `published_at` goes up as an ISO string with an offset. The input hands back
// a local wall clock time with no zone, so sending it raw would have the
// server read it as UTC and shift the date by the author's offset.
function attributes() {
  return {
    title: title.value.trim(),
    summary: summary.value.trim(),
    body: body.value,
    tags: tags.value,
    release_type: releaseType.value,
    video_url: videoUrl.value.trim(),
    published_at: publishedAt.value ? new Date(publishedAt.value).toISOString() : null,
    ...(manualVersion.value && version.value ? { version: version.value.trim() } : {}),
  }
}
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to    { opacity: 0; }
</style>
